import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box"
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import FormControl from "@mui/material/FormControl";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import GavelIcon from '@mui/icons-material/Gavel';
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tab from "@mui/material/Tab"
import { TabContext, TabList, TabPanel } from "@mui/lab";

import { isEmpty, isEqual, isNil } from "lodash";
import { isNull } from "lodash/lang";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import {addDays, addMonths, addWeeks} from "date-fns";

import planVersionService from "@plannel/services/data/plan-version-service";
import VersionConfigContent from "@plannel/components/VersionConfigContent";
import BaseConfigModal, { CONFIG_TAB_SX } from "@plannel/components/modal/BaseConfigModal";
import CodeUtil from "@plannel/pages/utils/code-util";
import DateUtil from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

import "@plannel/scss/components/ModalTab.scss"

// ── 패널 너비 상수 ──
const PANEL_WIDTH = { MP: 520, RP: 560 };

const { buckets, versionStatusCode, module } = CodeUtil;
const { RELEASED } = versionStatusCode;
const { RP, MP } = module;
const { snackBarMessageColor } = ColorUtil;
const { DAY, WEEK, MONTH } = buckets;

const CreateVersionModal = ({ width, t, viewName, moduleCd, modalOpen, setModalOpen, defaultValue, planVersions, createVersion, dpVersions, rpVersions, iconColor, setSnackInfo, useModules }) => {
  const reduxDispatch = useDispatch();
  const [tab, setTab] = useState("info");
  const [expanded, setExpanded] = useState(true);
  const [planPolicyEl, setPlanPolicyEl] = useState(null);
  const [rpPolicyEl, setRpPolicyEl] = useState(null);
  const isMp = moduleCd === MP;
  const isRp = moduleCd === RP;

  const masterData = { id: null, name: t("masterData"), descTxt: "" };
  const initData = { id: null, name: "", descTxt: "", sourceVersionId: null };
  const [newVersion, setNewVersion] = useState(initData);
  const [sourceVersion, setSourceVersion] = useState(masterData);

  const [sourceData, setSourceData] = useState([]);
  const [versionName, setVersionName] = useState("");
  const [versionDesc, setVersionDesc] = useState("");
  const configObj = useRef({});

  const getEndDateByUom = (startDate, uom, value) => {
    let endDate = startDate;
    if (isEqual(uom, DAY)) {
      endDate = addDays(startDate, value);
    } else if (isEqual(uom, WEEK)) {
      endDate = addWeeks(startDate, value);
    } else if (isEqual(uom, MONTH)) {
      endDate = addMonths(startDate, value);
    }

    return addDays(endDate, -1);
  }

  const normalizeMonthStart = (date) => {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // makePlanConfigObj 변경 시 VersionConfigContent 도 변경해주어야 함
  const makePlanConfigObj = props => {
    const { planStartDate, planEndDate, bucketUnit, planningRange, salesPeriodRef, demandPeriodRef, dpVersion, rpVersion, kitDemandMergeFlg,  demandDistributionPolicy, useTargetSupplyDays, noteFlg } = props;
    let startDate = planStartDate ? planStartDate : defaultValue.start;
    const bucket = bucketUnit ?? defaultValue.bucket;
    // 시작일: MONTH면 월초로 강제
    if (isEqual(bucket, MONTH)) {
      startDate = normalizeMonthStart(startDate);
    }
    //bucket에 따라서 변경해줘야 함
    let endDate = planEndDate;
    if (!planEndDate) {
      endDate = getEndDateByUom(startDate, bucket, defaultValue.range);
    }

    const dpVersionId = dpVersion ? dpVersion.id : null;
    const rpVersionId = rpVersion ? rpVersion.id : null;

    const planConfigObj = {
      BUCKET_UNIT: bucketUnit ?? defaultValue.bucket,
      PLANNING_RANGE: planningRange ?? defaultValue.range,
      PLAN_START_DATE: DateUtil.formatDate(startDate, "yyyy-MM-dd"),
      PLAN_END_DATE: DateUtil.formatDate(endDate, "yyyy-MM-dd"),
      DP_VERSION_ID: dpVersionId,
      RP_VERSION_ID: rpVersionId
    };

    if (isEqual(moduleCd, RP)) {
      planConfigObj.SALES_REF_START = salesPeriodRef ? salesPeriodRef[0] : defaultValue.salesRefStart;
      planConfigObj.SALES_REF_END = salesPeriodRef ? salesPeriodRef[1] : defaultValue.salesRefEnd;
      planConfigObj.DEMAND_REF_START = demandPeriodRef ? demandPeriodRef[0] : defaultValue.demandRefStartDate;
      planConfigObj.DEMAND_REF_END = demandPeriodRef ? demandPeriodRef[1] : defaultValue.demandRefEndDate;
      planConfigObj.KIT_DEMAND_MERGE_FLG = kitDemandMergeFlg ?? defaultValue.kitDemandMergeFlg ?? false;
      planConfigObj.MULTI_SOURCING_FLG = defaultValue.multiSourcingFlg ?? false;
      planConfigObj.DEMAND_DISTRIBUTION_POLICY = demandDistributionPolicy ?? defaultValue.demandDistributionPolicy;
      planConfigObj.BACKORDER = defaultValue.backorder;
      planConfigObj.IMMEDIATE_SHIPMENT = defaultValue.immediateShipment;
      planConfigObj.USE_TARGET_SUPPLY_DAYS = useTargetSupplyDays ?? defaultValue.useTargetSupplyDays ?? false;
      planConfigObj.NOTE_FLG = noteFlg ?? defaultValue.noteFlg ?? false;
    } else if (isEqual(moduleCd, MP)) {
      planConfigObj.MC = defaultValue.materialConst;
      planConfigObj.RC = defaultValue.resourceCapacity;
      planConfigObj.PS = defaultValue.planStrategy;
      planConfigObj.BACKORDER = defaultValue.backorder;
      planConfigObj.DEMAND_PRIORITY = defaultValue.demandPriority;
      planConfigObj.DATE_PRIORITY = defaultValue.datePriority;
      planConfigObj.FROZEN_RANGE = defaultValue.frozenRange;
      planConfigObj.SAFETY_STOCK_POLICY = defaultValue.safetyStockPolicy ?? "NONE";
      planConfigObj.KIT_PLAN_MODE = defaultValue.kitPlanMode;
      planConfigObj.ALLOW_SEMI_ORDER = defaultValue.allowSemiOrder;
      planConfigObj.ALT_RES_POLICY = defaultValue.altResPolicy;
      planConfigObj.ENABLE_ROLLING_PLAN = defaultValue.enableRollingPlan ?? false;
      if (defaultValue.TIME_BUCKETS) {
        planConfigObj.TIME_BUCKETS = defaultValue.TIME_BUCKETS;
      }
    }
    return planConfigObj;
  }

  // 소스 버전의 planConfigObj 를 그대로 복사하면, 그 버전에 없던 키는 새 버전에도 빈 채로 남는다.
  // Demand/Date Priority 는 MP 엔진(MasterPlanManager)이 필수로 읽는 값이라 기본값(MP Settings)으로 채워준다.
  const fillPriorityDefaults = planConfigObj => {
    if (!isEqual(moduleCd, MP) || !planConfigObj) {
      return planConfigObj;
    }

    const filled = { ...planConfigObj };
    if (isEmpty(filled.DEMAND_PRIORITY) && !isEmpty(defaultValue.demandPriority)) {
      filled.DEMAND_PRIORITY = defaultValue.demandPriority;
    }
    if (isEmpty(filled.DATE_PRIORITY) && !isEmpty(defaultValue.datePriority)) {
      filled.DATE_PRIORITY = defaultValue.datePriority;
    }

    return filled;
  }

  const createVersionName = () => {
    planVersionService.createVersionName({ moduleCd })
      .then(response => {
        setVersionName(response.data);
        setNewVersion({ ...newVersion, name: response.data, sourceVersionId: null })
      })
      .catch(e => {
        debug(e);
        const initialName = `${moduleCd}${DateUtil.getToday()}S001`
        setVersionName(initialName);
        setNewVersion({ ...newVersion, name: initialName, sourceVersionId: null })
      });
  }

  const initSettings = () => {
    const dpVersion = isEmpty(dpVersions) ? null : dpVersions[0];
    let rpVersion;

    if (dpVersion && rpVersions) {
      const childRpVersions = rpVersions.filter(version => version.parent?.id && isEqual(version.parent.id, dpVersion.id)); // dpVersion에 연결된 rpVersion 필터링
      rpVersion = isEmpty(childRpVersions) ? null : childRpVersions[0];
    }

    configObj.current = makePlanConfigObj({ dpVersion, rpVersion });


    const masterVersion = { ...masterData, planConfigObj: configObj.current };
    if (isEmpty(planVersions)) {
      setSourceData([masterVersion]);
    } else {
      setSourceData([masterVersion, ...planVersions]);
    }
    setSourceVersion(masterVersion);
    setNewVersion(initData);
    createVersionName();
  }

  const handleClose = action => {
    if (isEqual(action, "create")) {
      if (planVersions != null && !isEmpty(planVersions)) {
        const isDuplicated = planVersions.some(
          version => isEqual(version.name, newVersion?.name)
        );
        if (isDuplicated) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_DuplicateVersion") });
          return;
        }
      }
      if (!isEmpty(sourceVersion?.name) && !isEmpty(newVersion?.name)) {
        if (isEqual(configObj.current?.PLANNING_RANGE, "") || !isFinite(configObj.current?.PLANNING_RANGE) || configObj.current?.PLANNING_RANGE <= 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_PleaseEnterValues", { values: t("planRange") }) });
          return;
        }

        if (isEqual(moduleCd, MP) && !isEmpty(useModules) && useModules.includes(CodeUtil.roles.APP_RP) && isNil(configObj.current?.RP_VERSION_ID)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedModuleVersion", { module: t("rp") }) });
          return;
        }

        newVersion.moduleCd = moduleCd;
        newVersion.planConfigObj = configObj.current;
        newVersion.hasNote = configObj.current.NOTE_FLG ?? false;
        createVersion(newVersion);
        setModalOpen(false);
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_MissingInput") });
      }
    } else if (isEqual(action, "cancel")) {
      setModalOpen(false);
    }
  }

  const onChangeNewVerParams = (e) => {
    const name = e.target.name;
    const value = e.target.value;

    if (isEqual(name, "name")) {
      setVersionName(value);
    } else if (isEqual(name, "descTxt")) {
      setVersionDesc(value);
    }

    setNewVersion({ ...newVersion, [name]: value });
  }

  const onChangeSourceData = (newValue) => {
    const sourceConfig = newValue?.planConfigObj ? fillPriorityDefaults(newValue.planConfigObj) : makePlanConfigObj({});

    // 백필한 config 를 소스 버전에도 실어준다 — Detail 탭(VersionConfigContent)이 selectedPlanVersion 의
    // planConfigObj 를 읽어 configObj 를 다시 만들기 때문에, 원본을 그대로 넘기면 백필이 되돌려진다.
    setSourceVersion(newValue?.planConfigObj ? { ...newValue, planConfigObj: sourceConfig } : newValue);
    configObj.current = sourceConfig;

    setNewVersion({ ...newVersion, sourceVersionId: newValue.id, planConfigObj : configObj.current });
  }

  const handleChangeTab = (_, newValue) => {
    setTab(newValue);
  }

  useEffect(() => {
    if (modalOpen) {
      initSettings();
    } else {
      setTab("info");

      const reduxState = reduxUtil.createObjectState(viewName, { sourceVersion: null });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // 우측 패널이 펼쳐지면 모달이 넓어진다 — 프리셋으로는 표현이 안 돼 width 를 직접 계산해 넘긴다
  const sidePanelWidth = (isMp || isRp) && tab === "detail" && expanded ? (isMp ? PANEL_WIDTH.MP : PANEL_WIDTH.RP) : 0;

  return (
    /* 아이콘은 PlayArrow — 저장이 아니라 생성이다. DP Process Mgmt 의 "Start DP" 버튼과 맞춘 것.
       주의: 이 프로젝트의 RunIcon 도 PlayArrow 라 Run 모달의 실행 버튼과 같은 아이콘이 된다. */
    <BaseConfigModal
      open={modalOpen}
      onClose={() => handleClose("cancel")}
      title={t("createNewVersion")}
      titleAdornment={(isMp || isRp) && tab === "detail" ? (
        <IconButton size="small" onClick={() => setExpanded(prev => !prev)} sx={{ ml: 1, color: "cornflowerblue" }} aria-label={expanded ? "collapse" : "expand"}>
          {expanded ? <KeyboardArrowLeftIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        </IconButton>
      ) : null}
      onSave={() => handleClose("create")}
      saveLabel={t("create")}
      saveIcon={<PlayArrowIcon />}
      width={(width ?? 540) + sidePanelWidth + 32}
      paperSx={{ transition: "width 0.3s ease" }}
    >
        {/* 탭 규격은 설정 모달 표준(CONFIG_TAB_SX + 아래 hairline)을 따른다 — Item Dimension Setting 과 동일.
            원래는 ModalTab.scss 로 타일을 채운 브라우저 탭 모양이었고, 이 파일만 그 스타일을 썼다. */}
        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChangeTab} sx={CONFIG_TAB_SX}>
              <Tab label={t("info")} value="info" />
              <Tab label={t("detail")} value="detail" />
            </TabList>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "row" }}>
            {/* ── 좌측: 기존 영역 ── */}
            <Box className="modal-tab" sx={{ width: width ?? 540, flexShrink: 0, display: "flex", flexDirection: "column" }}>
              <TabPanel className="tab-panel" value="info">
                <FormControl size="small" sx={{ mt: 1, width: "100%" }} key={"source"}>
                  <VersionAutocomplete
                    publishToChat={false}
                    options={sourceData}
                    value={sourceVersion}
                    onChange={(_, newValue) => onChangeSourceData(newValue)}
                    label={t("sourceData")}
                    getIcon={(option) => (isNull(option.id) ? null : (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon))}
                    getIconColor={iconColor}
                    getTooltip={(option) => {
                      const c = option?.planConfigObj;
                      return c?.PLAN_START_DATE && c?.PLAN_END_DATE ? `${c.PLAN_START_DATE} ~ ${c.PLAN_END_DATE}` : null;
                    }}
                    showInputIcon={false}
                    sx={{ mr: 0, minWidth: 0, width: "100%" }}
                  />
                </FormControl>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    name="name"
                    sx={{ width: "100%" }}
                    label={t("versionName")}
                    variant="outlined"
                    value={versionName}
                    onChange={onChangeNewVerParams}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <TextField
                    name="descTxt"
                    sx={{ width: "100%" }}
                    label={t("descTxt")}
                    variant="outlined"
                    multiline
                    rows={2}
                    value={versionDesc}
                    onChange={onChangeNewVerParams}
                  />
                </Box>
              </TabPanel>
              <TabPanel className="tab-panel" value="detail">
                <VersionConfigContent t={t} viewName={viewName} moduleCd={moduleCd} defaultValue={defaultValue}
                  selectedPlanVersion={sourceVersion} dpVersions={dpVersions} rpVersions={rpVersions} configObj={configObj} useModules={useModules}
                  planPolicyContainer={isMp ? planPolicyEl : undefined}
                  rpPolicyContainer={isRp ? rpPolicyEl : undefined}
                  fitContent />
              </TabPanel>
            </Box>

            {/* ── 우측: 확장 패널 (수평 슬라이드) ── */}
            {(isMp || isRp) && tab === "detail" && (
              <Box sx={{
                width: sidePanelWidth,
                overflow: "hidden",
                transition: "width 0.3s ease",
                flexShrink: 0,
              }}>
                <Box sx={{ width: isMp ? PANEL_WIDTH.MP : PANEL_WIDTH.RP, p: 2, boxSizing: "border-box" }}>
                  {isMp
                    ? <Box ref={setPlanPolicyEl} sx={{ width: "100%", boxSizing: "border-box" }} />
                    : <Box ref={setRpPolicyEl} sx={{ width: "100%", boxSizing: "border-box" }} />
                  }
                </Box>
              </Box>
            )}
          </Box>
        </TabContext>
    </BaseConfigModal>
  );
};

export default withTranslation()(CreateVersionModal);
